import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Phone, BatteryCharging, Truck, CircleDot, Fuel, Car, KeyRound, ShieldCheck } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, insurancePartners } from '../mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const getIconComponent = (iconName) => {
    const iconMap = {
      wrench: LucideIcons.Wrench,
      sparkles: LucideIcons.Sparkles,
      paintbrush: LucideIcons.Paintbrush,
      wind: LucideIcons.Wind,
      shield: LucideIcons.Shield,
      fileText: LucideIcons.FileText,
      truck: LucideIcons.Truck,
      zap: LucideIcons.Zap
    };
    return iconMap[iconName] || LucideIcons.Wrench;
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Our Premium
              <br />
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive car care solutions tailored for luxury and premium vehicles
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              return (
                <motion.div
                  key={service.id}
                  className="group relative bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-red-600 hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  onClick={() => setSelectedService(service)}
                >
                  {/* Number Badge */}
                  <div className="absolute top-6 right-6 w-12 h-12 bg-gray-100 group-hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-300">
                    <span className="text-gray-600 group-hover:text-white font-bold transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>

                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-gray-700">
                        <CheckCircle2 className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a href="tel:+918866689002" className="block">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white group-hover:shadow-lg transition-all duration-300">
                      Book Service Now: 8866689002
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CSA - Roadside Assistance Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-900 to-red-900/40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Heading + Contact */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-full text-red-400 text-sm font-semibold mb-6 uppercase tracking-wider">
                <span className="relative flex h-2.5 w-2.5 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                CSA Services &mdash; Available 24/7
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Roadside <span className="text-red-500">Assistance</span>
                <br />Anytime, Anywhere
              </h2>
              <p className="text-lg text-gray-300 mb-8 max-w-lg">
                Stuck on the road? Our Carz Street Assistance (CSA) team is just a call away, round the clock, to get you moving again.
              </p>

              <a
                href="tel:+918980003797"
                data-testid="csa-call-button"
                className="inline-flex items-center gap-4 bg-white rounded-2xl p-5 shadow-2xl hover:shadow-red-600/30 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">24/7 Helpline</div>
                  <div className="text-2xl font-bold text-gray-900">+91 89800 03797</div>
                </div>
              </a>

              {/* Annual Membership Plan */}
              <div className="mt-6 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 shadow-2xl max-w-md">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/20">
                  <div>
                    <div className="text-white/80 text-xs uppercase tracking-wider font-semibold">Annual Membership</div>
                    <div className="text-white text-lg font-bold">Road Assistance Plan</div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-white">&#8377;999</div>
                    <div className="text-white/80 text-xs">per year</div>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {[
                    'Battery Issues at cost',
                    'Towing Service with Crane at cost',
                    'Tyre Puncture Service at cost',
                    'Pick up & Drop for Service, Insurance & Lost Key at cost'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-white/95 text-sm">
                      <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Right: Services List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { icon: BatteryCharging, label: 'Battery Issues' },
                  { icon: Truck, label: 'Towing Service with Crane*' },
                  { icon: CircleDot, label: 'Tyre Puncture Service' },
                  { icon: Fuel, label: 'Empty Fuel at cost (5 Litre)*' },
                  { icon: Car, label: 'Cab Service at Cost*' },
                  { icon: KeyRound, label: 'Pick up & Drop for Service, Insurance & Lost Key*' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-400" />
                    </div>
                    <span className="text-gray-200 font-medium text-sm leading-snug pt-1.5">{item.label}</span>
                  </motion.div>
                ))}
              </div>
              <p className="text-gray-400 text-xs italic mt-6 text-center">
                *Terms &amp; Conditions Applied
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Carz Street?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Premium service with unmatched attention to detail
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: LucideIcons.Award,
                title: 'Certified Technicians',
                description: 'Factory-trained experts for all premium brands'
              },
              {
                icon: LucideIcons.Shield,
                title: 'Quality Guarantee',
                description: '6-month warranty on all services and parts'
              },
              {
                icon: LucideIcons.Clock,
                title: 'Quick Turnaround',
                description: 'Fast and efficient service without compromising quality'
              },
              {
                icon: LucideIcons.Package,
                title: 'Genuine Parts',
                description: 'Only OEM and premium quality replacement parts'
              },
              {
                icon: LucideIcons.Car,
                title: 'Pickup & Drop',
                description: 'Complimentary pickup and delivery service'
              },
              {
                icon: LucideIcons.Headphones,
                title: '24/7 Support',
                description: 'Round-the-clock customer support and assistance'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <feature.icon className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Partners Section */}
      <section className="py-24" id="insurance">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-semibold mb-4">
              <ShieldCheck className="w-4 h-4" />
              Cashless Insurance Claims
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Insurance Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are tied up with India's leading insurance companies to make your claim process smooth, fast and completely hassle-free.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {insurancePartners.map((company, index) => (
              <motion.div
                key={index}
                data-testid={`insurance-partner-${index}`}
                className="group bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:border-red-600 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (index % 3) * 0.08 }}
                whileHover={{ y: -4 }}
              >
                <div className="w-11 h-11 bg-gray-100 group-hover:bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                  <ShieldCheck className="w-6 h-6 text-red-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <span className="text-gray-800 font-semibold text-sm leading-snug">{company}</span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-gray-500 mt-10"
          >
            Need help with your insurance claim? Our team handles the paperwork for you.{' '}
            <a href="tel:+919925025351" className="text-red-600 font-semibold hover:underline">Call +91 99250 25351</a>
          </motion.p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-600 to-red-800">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need a Custom Service Package?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Contact us for a personalized quote tailored to your vehicle's needs
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Get a Custom Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;